import { unref, type MaybeRef } from 'vue';
import { useStringFunctions } from './useStringFunctions';
import { AsKeyed } from '@apparatix/types';
import { FileExtensionToMimeType } from '@apparatix/constants/FileExtensionToMimeType';

export const useUtilities = () => {
  const { isEmptyString } = useStringFunctions();

  const forKeys = (obj: object, fn: (key: string, value: any) => void) => {
    Object.keys(obj).forEach(k => {
      if (!Object.hasOwn(obj, k)) return;
      fn(k, obj[k]);
    });
  };

  const mapValues = (obj: object, mapFn: (key: string, value: any) => any): any[] => {
    const values: any[] = [];
    forKeys(obj, (k, v) => {
      values.push(mapFn(k, v));
    });

    return values;
  };

  const randomNumber = (min = 0, max = Number.MAX_SAFE_INTEGER) => Math.random() * (max - min) + min;

  /**
   * @description Get weighted random numbers, where n% of the results are a pre-specified number,
   * and the remainder are random. For example, if getWeightedRandomNumber(5, 0, 100, 2) were run
   * 100 times, approximately 50% of the returned numbers would be 5, and the remaining 50% would
   * be completely random numbers (potentially including 5, since the preferred number isn't excluded from a random selection)
   * @param preferredOutcome the number you'd like to weight results toward
   * @param min minimum number, inclusive
   * @param max maximum number, inclusive
   * @param oneInNOdds the chances, expressed as a whole number, that the preferred result will be returned. eg 2 is a 50% chance, 3 is a 33% chance
   */
  const getWeightedRandomNumber = (preferredOutcome: number, min: number, max: number, oneInNOdds: number = 3) => {
    const roll = Math.floor(Math.random() * oneInNOdds) + 1;
    if (roll === 1) {
      return preferredOutcome;
    }

    return Math.floor(randomNumber(min, max));
  };

  const isValidNumber = (test: string | number): test is number => {
    if (typeof test === 'number') return true;
    return !isNaN(parseInt(test, 10));
  };

  const areAllFieldsEmpty = (obj: object) => {
    let anyFieldHasValue = false;
    forKeys(obj, (_, value) => {
      if (value) {
        anyFieldHasValue = true;
        return;
      }
      if (anyFieldHasValue) {
        return;
      }
    });

    return !anyFieldHasValue;
  };

  const isNumberEmpty = (test?: number, zeroIsEmpty: boolean = false, negativeIsEmpty: boolean = false) => {
    if (test === null || test === undefined) return true;
    if (isNaN(test)) return true;

    if (zeroIsEmpty && negativeIsEmpty) return test <= 0;
    if (zeroIsEmpty && !negativeIsEmpty) return test === 0;
    if (!zeroIsEmpty && negativeIsEmpty) return test < 0;
    return false;
  };

  /**
   *
   * @description Takes an object, and returns a new object containing only fields from the original object that have a value
   * @param input Source object
   * @param {boolean} [removeEmptyArray=true] if true, will remove array fields if they are empty
   * @param {boolean} [removeEmptyObject=true] if true, will remove objects that are defined but have no fields. Null/undefined objects are always removed
   * @param {boolean} [looseStringCheck=true] if true, will remove string fields that are not empty, but contain only whitespace. Empty strings are always removed
   * @returns A new object with the same shape as the input object, but only containing fields that had values
   */
  const getOnlyPopulatedFields = <T extends object>(input: T, removeEmptyArray: boolean = true, removeEmptyObject: boolean = true, looseStringCheck: boolean = true): T => {
    if (!input) return null;
    if (Array.isArray(input) || typeof input !== 'object') {
      console.error(`getOnlyPopulatedFields expects input type to be object, but got ${typeof input}`);
      return null;
    }

    const result: T = {} as T;
    Object.keys(input).filter(key => Object.hasOwn(input, key)).forEach(key => {
      const currentValue = input[key];
      const type = typeof currentValue;

      if (currentValue === null || currentValue === undefined) {
        return;
      }

      if (type === 'string') {
        if (!isEmptyString(currentValue, looseStringCheck)) {
          result[key] = currentValue;
        }
        return;
      }

      if (Array.isArray(currentValue)) {
        if (removeEmptyArray) {
          if (currentValue.length) {
            result[key] = currentValue;
          }
        } else {
          result[key] = currentValue;
        }
        return;
      }

      if (type === 'object') {
        if (removeEmptyObject) {
          if (Object.keys(currentValue).length) {
            result[key] = currentValue;
          }
        } else {
          result[key] = currentValue;
        }
        return;
      }

      result[key] = currentValue;
    });

    if (Object.keys(result).length === 0) {
      return null;
    }

    return result;
  };

  /**
   * Test whether something is an object
   * @param test value to test
   * @param {boolean} [allowArray=false] In javascript arrays are objects, but typically for tests like this we don't want to consider them as such
   * @returns true if the value is a non-null object
   */
  const isObject = (test?: unknown, allowArray: boolean = false): test is object => {
    const baseTest = typeof test === 'object' && test !== null;

    if (!allowArray) {
      return baseTest && !Array.isArray(test);
    }

    return baseTest;
  };

  const objectContainsFields = (test: object, fields: Array<number | string | symbol> | string | number | symbol) => {
    if (!isObject(test)) return false;
    if (!Array.isArray(fields)) {
      return Object.hasOwn(test, fields);
    }

    return fields.every(field => Object.hasOwn(test, field));
  };

  type ArrayHasValuesOptions =
  | {
    exactly: number,
    min?: never,
    max?: never,
    limit?: never,
  }
  | {
    limit: number,
    min?: never,
    max?: never,
    exactly?: never,
  }
  | {
    min?: number,
    max?: number,
    limit?: never,
    exactly?: never,
  };

  const arrayHasValues = (test?: MaybeRef<unknown>, options?: ArrayHasValuesOptions) => {
    if (!test) return false;

    if (options) {
      const fields: Array<keyof ArrayHasValuesOptions> = ['exactly', 'limit', 'max', 'min'];
      fields.forEach(field => {
        if (field in options) {
          const type = typeof options[field];
          if (type !== 'number') {
            throw new Error(`Expected number for options.${field} but received ${type}`);
          }
        }
      });
    }

    const unwrapped = unref(test);

    if (!Array.isArray(unwrapped)) {
      return false;
    }

    const { length } = unwrapped;

    const defaultOptions: ArrayHasValuesOptions = {
      min: 1,
      max: Number.POSITIVE_INFINITY,
    };

    const opts = { ...defaultOptions, ...options };

    // Check on type here instead of existence in case provided value is 0
    // ie an (unusual but valid) usecase could be arrayHasValues(arr, { exactly: 0 })
    if (typeof opts.exactly === 'number') {
      return length === opts.exactly;
    }

    if (opts.limit) {
      if (opts.limit < 1) {
        throw new Error('options.limit cannot be less than 1. If you want to ensure an array has no values, use { exactly: 0 }');
      }

      opts.min = 1;
      opts.max = opts.limit;
    }

    return length >= opts.min && length <= opts.max;
  };

  /**
   *
   * Note that this function will return FALSE if the provided test is not an object
   * This is intended to be used on something you already know is an object
   */
  const isEmptyObject = (test: object, checkArray: boolean = true) => {
    if (typeof test !== 'object') {
      return false;
    }

    if (test === null) {
      return true;
    }

    if (checkArray && Array.isArray(test)) {
      return !arrayHasValues(test);
    }

    return Object.keys(test).filter(key => Object.hasOwn(test, key)).length === 0;
  };

  const deepClone = <T>(toClone: T): T => JSON.parse(JSON.stringify(toClone));

  /**
   * Safely call a function without wrapping in `if (typeof fn === 'function')`
   */
  const safeInvoke = <TArgs extends any[], TReturn>(fn: ((...args: TArgs) => TReturn) | (() => TReturn), ...args: TArgs): TReturn => {
    if (typeof fn !== 'function') return undefined;
    if (args) {
      // eslint-disable-next-line prefer-spread
      return fn.apply(null, args);
    }

    return fn();
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  const isFunction = (test?: unknown): test is Function => typeof test === 'function';

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  const isAsyncFunction = (test?: Function) => isFunction(test) && test?.constructor?.name === 'AsyncFunction';

  /**
   *
   * @description takes an array of objects, and adds a unique key to each object
   * @property {boolean} [inPlace=false] if true, adds the keys directly to the items in the array instead of cloning the objects
   */
  const asKeyedItems = <T extends object>(items: T[], inPlace: boolean = false): AsKeyed<T>[] => items?.map(item => {
    if (inPlace) {
      (item as AsKeyed<T>).key = crypto.randomUUID();
      return item as AsKeyed<T>;
    }

    return { ...item, key: crypto.randomUUID() };
  });

  /**
   * @description Picks the specified keys from one object into a new one
   * @param source Source object
   * @param keys Array of keys of source object that will be plucked into the return object
   * @returns A new object containing only the provided fields from the source object
   */
  const partial = <T extends object>(source: T, keys: Array<keyof T>): {[K in keyof T]?: T[K] } => Object.fromEntries(
    keys.map(key => {
      const value = source[key];

      if (typeof value === 'object' && value !== null) {
        return [key, deepClone(value)];
      }
      return [key, value];
    }),
  ) as {[K in keyof T]?: T[K] };

  /**
   * @description Clones an object, omitting specified keys
   * @param source Source object
   * @param keys Array of keys to omit from the return object
   * @returns new object with all of the fields of the source object, omitting the specified keys
   */
  const omit = <T extends object>(source: T, keys: Array<keyof T>, keepUndefined: boolean = true): Partial<T> => {
    if (!arrayHasValues(keys)) {
      return deepClone(source);
    }

    const keysToOmit = new Set<keyof T>([...keys]);
    const result: Array<[key: string, value: any]> = [];

    Object.keys(source).filter(key => !keysToOmit.has(key as keyof T))
      .forEach(key => {
        const value = source[key];

        if (isObject(value, true)) {
          result.push([key, deepClone(value)]);
          return;
        }

        if (value === undefined && keepUndefined) {
          result.push([key, undefined]);
          return;
        }

        result.push([key, value]);
      });

    return Object.fromEntries(result) as Partial<T>;
  };

  /**
   * @description Use to show errors/warnings that should only appear when running the site in dev mode
   * @param callPredicate predicate function to determine whether the message should be shown. Can also pass `true` to always show
   * @param message error message or instance of Error
   * @param type Optional type to determine how the message is shown. Options are console log/warn/error, or throw an error. Defaults to console.error if not provided
   */
  const developerWarning = (callPredicate: (() => boolean) | boolean, message: string | Error, type?: 'console:log' | 'console:warn' | 'console:error' | 'throw') => {
    const shouldShow = typeof callPredicate === 'function'
      ? callPredicate()
      : callPredicate;

    if (import.meta.env.DEV && shouldShow) {
      const errorMessage = message instanceof Error
        ? message.message
        : message;

      switch (type) {
        case 'console:log':
          console.log(errorMessage);
          break;
        case 'console:warn':
          console.warn(errorMessage);
          break;
        case 'console:error':
          console.error(errorMessage);
          break;
        case 'throw':
          if (message instanceof Error) {
            throw message;
          } else {
            throw new Error(message);
          }
        default:
          console.error(errorMessage);
      }
    }
  };

  /**
   *
   * @description combines arrays into a single new array. Empty arrays are discarded
   */
  const combineArrays = <T>(...args: Array<T[]>): T[] => [...args.filter(arg => arrayHasValues(arg)).flatMap(arg => arg)];

  /**
   *
   * @description sometimes you just don't feel like doing anything
   * (the actual intent of this function is for use as a default argument for Vue's inject,
   * or other similar use cases)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-empty-function
  const noop = (...args: any[]) => {};

  /**
   * @description Programmatically initiate a file download
   * @param fileData file data to download, or URL where file data is located
   * @param filename name of the file to download
   * @param mimeType MIME type of file
   * @param dataIsUrl if true, will fetch the URL provided in the fileData arg, and use that file contents as the file data
   */
  const downloadFile = async (fileData: BlobPart | string, filename: string, mimeType: string, dataIsUrl: boolean = false) => {
    let data: BlobPart;
    if (typeof fileData === 'string' && dataIsUrl) {
      const response = await fetch(fileData);
      const blobContent = await response?.blob();
      if (!blobContent) {
        throw new Error('Error retrieving file contents');
      }

      data = blobContent;
    } else {
      data = fileData;
    }

    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   *
   * @description Attempt to get MIME type for a file
   * @returns MIME type if able to be determined, falls back to empty string
   */
  const getMimeTypeForFilename = (filename: string) => {
    if (!filename || typeof filename !== 'string') {
      return '';
    }

    const extension = filename.split('.').pop();

    if (extension in FileExtensionToMimeType) {
      return FileExtensionToMimeType[extension];
    }

    return '';
  };

  /**
   * @description used to wait a specified number of milliseconds in an async function. This function should ONLY be used
   * for things like waiting for an animation to complete, or to run something after a short timeout. It should NOT be used
   * as an escape hatch to work around async issues or timing bugs
   * @param ms amount of milliseconds to wait
   */
  const sleep = (ms: number) => new Promise(resolve => setTimeout(() => resolve(true), ms));

  /**
   *
   * @description removes an item from an array if it matches the provided predicate.
   * If an array contains multiple items that meet the predicate criteria, note that only the first match will be removed
   */
  const removeArrayElement = <T>(sourceArray: MaybeRef<T[]>, predicate: (item: T) => boolean): void => {
    if (!arrayHasValues(sourceArray)) return;

    const source = unref(sourceArray);

    const idx = source.findIndex(predicate);
    if (idx !== -1) {
      source.splice(idx, 1);
    }
  };

  /**
   *
   * @description inserts an item into the target array at a given index
   * if the provided index is longer than the array length, javascript inserts the item at the end of the array
   */
  const insertArrayItemAtIndex = <T>(item: T, index: number, target: T[]) =>
    target.splice(index, 0, item);

  /**
   *
   * @description swaps items at given indices in an array
   */
  const swapArrayPositions = <T>(arr: T[], index1: number, index2: number): void => {
    [arr[index1], arr[index2]] = [arr[index2], arr[index1]];
  };

  /**
   * Adds an additional property to an object, and returns an object with a
   * type definition for the new field
   * @param obj original object
   * @param propertyName name of new property
   * @param value value of new property
   * @param {boolean} [writable=true] whether the new property should be writable after setting the value
   * @param {boolean } [asCopy=false] returns a copy of the object instead of adding the new property to the original object
   */
  const withAdditionalProperty = <T, U extends object, V extends PropertyKey>(
    obj: U,
    propertyName: V,
    value: T,
    writable: boolean = true,
    asCopy: boolean = false,
  ): U & {[K in V]: T } => {
    if (!isObject(obj)) {
      throw new Error('value passed to withAdditionalProperty must be a non-null, non-array object');
    }

    const result = asCopy ? deepClone(obj) : obj;

    Object.defineProperty(result, propertyName, {
      value: value,
      writable: writable,
      enumerable: true,
      configurable: writable,
    });

    return result as U & {[K in V]: T };
  };

  /**
   * Returns only an object's own keys
   * @param obj source object
   * @returns array of the object's own keys
   */
  const ownKeys = <T extends object>(obj: T): Array<keyof T> => Object.keys(obj).filter(key => Object.hasOwn(obj, key)) as Array<keyof T>;

  /**
   * Tests whether two arrays contain identical values, in identical order. If the order of values is not important,
   * the arrays should be sorted before being passed to this function. If an array element contains another array, this
   * function is called recursively on that array.
   * @param a first array
   * @param b second array
   * @param strictObjectEquality if true, when an array element is an object, equality is checked via strict ===. If false, equality is checked via {@linkcode areObjectsEqual}
   * @returns true if both arrays are the same length, and contain identical items in identical order
   */
  const arraysContainSameValues = <T>(a: T[], b: T[], strictObjectEquality?: boolean): boolean => {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }

    for (let i=0; i<a.length; i++) {
      const aItem = a[i];
      const bItem = b[i];
      if (typeof aItem !== typeof bItem) {
        return false;
      }

      if ((Array.isArray(aItem) && !Array.isArray(bItem)) || (!Array.isArray(aItem) && Array.isArray(bItem))) {
        return false;
      }

      if (isObject(aItem) && isObject(bItem)) {
        const areEqual = strictObjectEquality
          ? aItem === bItem
          : areObjectsEqual(aItem, bItem);

        if (!areEqual) {
          return false;
        }

        continue;

      }

      if (Array.isArray(aItem) && Array.isArray(bItem)) {
        const areChildArraysEqual = arraysContainSameValues(aItem, bItem);

        if (!areChildArraysEqual) {
          return false;
        }

        continue;
      }

      if (aItem !== bItem) {
        return false;
      }
    }

    return true;
  };

  /**
   * Tests whether two objects contain identical fields with identical values, recursing
   * over any child properties that are arrays or objects. This function is used for cases
   * where the objects are not technically pointing to the same object in memory, but are
   * effectively identical
   * const original = { fruit: 'Apple', price: 1.99 };
   * const copy = { ...original };
   * if (copy === original) would fail here
   * if (areObjectsEqual(original, copy)) would pass
   * @param a first object
   * @param b second object
   * @returns true if both objects contain identical fields, and all fields have identical values. The order of the fields is not taken into account, only the names and values
   */
  const areObjectsEqual = <T extends object>(a: T, b: T): boolean => {
    if (!a || !b || !isObject(a, true) || !isObject(b, true)) {
      return false;
    }

    const aKeys = ownKeys(a);
    const bKeys = ownKeys(b);

    const bothHaveSameKeys = aKeys.length === bKeys.length && aKeys.every(key => ((key in b) && typeof a[key] === typeof b[key]));

    if (!bothHaveSameKeys) {
      return false;
    }

    for (const key of aKeys) {
      const aValue = a[key];
      const bValue = b[key];

      if (isObject(aValue) && isObject(bValue)) {
        if (!areObjectsEqual(aValue, bValue)) {
          return false;
        }

        continue;
      }

      if (Array.isArray(aValue) && Array.isArray(bValue)) {
        if (!arraysContainSameValues(aValue, bValue)) {
          return false;
        }

        continue;
      }

      if ((Array.isArray(aValue) && !Array.isArray(bValue)) || (!Array.isArray(aValue) && Array.isArray(bValue))) {
        return false;
      }

      if (a[key] !== b[key]) {
        return false;
      }
    }

    return true;
  };

  return {
    areAllFieldsEmpty,
    areObjectsEqual,
    arrayHasValues,
    arraysContainSameValues,
    asKeyedItems,
    combineArrays,
    deepClone,
    developerWarning,
    downloadFile,
    forKeys,
    getMimeTypeForFilename,
    getOnlyPopulatedFields,
    getWeightedRandomNumber,
    insertArrayItemAtIndex,
    isAsyncFunction,
    isEmptyObject,
    isFunction,
    isNumberEmpty,
    isObject,
    isValidNumber,
    mapValues,
    noop,
    objectContainsFields,
    omit,
    ownKeys,
    partial,
    randomNumber,
    removeArrayElement,
    safeInvoke,
    sleep,
    swapArrayPositions,
    withAdditionalProperty,
  };
};
